import { Button } from "@sellar-io/components";
import { useIntercom } from "react-use-intercom";
import { useMediaQuery } from "usehooks-ts";
import { Icon, SpinnerLegacy } from "../../components";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { useSellarUser } from "../../hooks";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { usePaymentLink } from "../../hooks/api/v2/paymentLink/usePaymentLink";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { PAYMENT_LINK } from "../../constants/routes";
import { CheckoutSuccessSummary } from "../stockList/checkoutV2/CheckoutV2SuccessPageWithFeed";
import { useCheckoutSuccessPageData } from "../stockList/checkoutV2/lib/useCheckoutSuccessPageData";
import { SellarCreditPaymentLinkContent } from "./SellarCreditPaymentLinkContent";
import { useLegalEntityCompanyCreditInfo } from "../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { DISCOVER, ORDERS } from "../../constants/routes";
import { useNavigate } from "react-router";

export const SellarCreditPaymentLink = () => {
  const navigate = useNavigate();
  const { signOut } = useSellarUser();
  const intercom = useIntercom();
  const orderIdParam = useParamAsNumber(PAYMENT_LINK, "orderId");

  const isDesktop = useMediaQuery("(min-width: 770px)");
  if (!orderIdParam) throw new Error("orderId is required");

  const paymentLinkQuery = usePaymentLink(orderIdParam);

  const companyCreditInfoQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: paymentLinkQuery.data?.Retailer.id ?? 0,
    },
    {
      enabled: Boolean(paymentLinkQuery.data?.Retailer.id),
    },
  );

  const {
    supplierName,
    supplierLogoUrl,
    orderId,
    orderTotal,
    orderStatus,
    orderItems,
    orderVatPercentage,
    paymentIntentId,
    paymentStatus,
    paymentType,
    stripeAccountId,
    retailerBusinessId,
    supplierId,
    twoOrder,
    hasPaymentLink,
  } = useCheckoutSuccessPageData({ orderId: orderIdParam });

  if (paymentLinkQuery.isPending || companyCreditInfoQuery.isPending) {
    return (
      <div className="flex w-full items-center justify-center md:min-h-screen">
        <SpinnerLegacy />
      </div>
    );
  }

  if (paymentLinkQuery.isError) {
    throw new Error(
      apiV2AssertError(paymentLinkQuery.error)?.response?.data.message ??
        "Unknown error",
    );
  }

  const companyCreditInfo = companyCreditInfoQuery.data;
  const paymentLink = paymentLinkQuery.data;

  if (!paymentLink) throw new Error("Payment link not found");

  const paymentIsHandled = !!paymentLink.Payment?.deferredPaymentId;
  return (
    <>
      <NavigationBar
        title={"Payment Link"}
        leftIcon={"INFORMATION_OUTLINE"}
        leftIconText={isDesktop ? "Support" : undefined}
        onClickLeftIcon={() => intercom.show()}
        rightComponent={
          <Button
            color={"transparent"}
            onClick={() => {
              signOut();
            }}
          >
            {" "}
            <Icon className={"text-red-500"} icon={"LOGIN_VARIANT"} />
            <span className="hidden sm:block">Log out</span>
          </Button>
        }
      />
      {paymentIsHandled ? (
        <div className={"flex items-center justify-center"}>
          <div className={"max-w-lg"}>
            <CheckoutSuccessSummary
              supplierName={supplierName}
              supplierLogoUrl={supplierLogoUrl}
              stripeAccountId={stripeAccountId}
              paymentIntentId={paymentIntentId}
              orderId={orderId}
              orderItems={orderItems}
              orderStatus={orderStatus}
              orderTotal={orderTotal}
              orderVatPercentage={orderVatPercentage}
              paymentStatus={paymentStatus}
              paymentType={paymentType}
              retailerBusinessId={retailerBusinessId}
              supplierId={supplierId}
              twoOrder={twoOrder}
              hasPaymentLink={hasPaymentLink}
              onClickBackToMarket={() => {
                navigate(DISCOVER);
              }}
              onClickReviewOrEditOrder={() => {
                navigate(`${ORDERS}/${orderId}?tab=All+orders`);
              }}
            />
          </div>
        </div>
      ) : (
        <div className="flex w-full items-center justify-center md:min-h-screen">
          <SellarCreditPaymentLinkContent
            paymentLink={paymentLink}
            companyCreditInfo={companyCreditInfo}
          />
        </div>
      )}
    </>
  );
};
